import React from "react";
import { Phone, Mail, MapPin, Facebook, Youtube } from "lucide-react";
import { COMPANY, SERVICES, LOGOS } from "@/lib/constants";
import { BBBSeal } from "@/components/site/BBBSeal";

export const Footer = () => {
  return (
    <footer className="relative bg-[#07111C] border-t border-slate-900 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-14 border-b border-slate-900">
          <div className="md:col-span-5">
            <img
              src={LOGOS.full}
              alt="All Day Power Washing"
              className="h-44 w-auto object-contain mb-4 drop-shadow-[0_8px_24px_rgba(230,180,69,0.18)]"
            />
            <p className="text-slate-400 mt-4 max-w-md">
              {COMPANY.slogan} — Veteran owned &amp; operated. Serving{" "}
              {COMPANY.serviceArea}.
            </p>

            {/* Trust badges: BBB accreditation seal */}
            <div className="mt-6 flex items-center gap-4">
              <BBBSeal />
              <div className="text-[10px] font-heading uppercase tracking-widest text-slate-500 leading-relaxed max-w-[180px]">
                BBB Accredited<br />
                <span className="text-slate-400">Licensed &amp; Insured</span>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <a
                href={COMPANY.social.facebook}
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-sm border border-slate-800 hover:border-[#1E88E5] flex items-center justify-center text-slate-300 hover:text-white transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={COMPANY.social.youtube}
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="YouTube"
                data-testid="social-youtube"
                className="w-10 h-10 rounded-sm border border-slate-800 hover:border-[#1E88E5] flex items-center justify-center text-slate-300 hover:text-white transition-colors"
              >
                <Youtube className="w-4 h-4" />
              </a>
              <a
                href={COMPANY.social.google}
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Google Business Profile"
                data-testid="social-google"
                className="w-10 h-10 rounded-sm border border-slate-800 hover:border-[#1E88E5] flex items-center justify-center text-slate-300 hover:text-white transition-colors text-[10px] font-heading font-black tracking-widest"
              >
                G
              </a>
              <a
                href={COMPANY.social.bbb}
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Better Business Bureau"
                data-testid="social-bbb"
                className="w-10 h-10 rounded-sm border border-slate-800 hover:border-[#1E88E5] flex items-center justify-center text-slate-300 hover:text-white transition-colors text-[10px] font-heading font-black tracking-widest"
              >
                BBB
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] mb-4">
              Services
            </div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <a href="/#services" className="text-slate-300 hover:text-white transition-colors text-sm">
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
            <div className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] mt-8 mb-4">
              Service Areas
            </div>
            <ul className="space-y-3">
              <li>
                <a href="/" className="text-slate-300 hover:text-white transition-colors text-sm" data-testid="footer-link-colorado-springs">
                  Colorado Springs
                </a>
              </li>
              <li>
                <a href="/denver" className="text-slate-300 hover:text-white transition-colors text-sm" data-testid="footer-link-denver">
                  Denver Metro
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] mb-4">
              Contact
            </div>
            <address className="not-italic space-y-4">
              <a href={COMPANY.phoneHref} className="flex items-center gap-3 text-white hover:text-[#E6B445] transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-[#1E88E5]" /> {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#1E88E5]" /> {COMPANY.email}
              </a>
              <a
                href={COMPANY.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address"
                className="flex items-start gap-3 text-slate-300 hover:text-white transition-colors"
              >
                <MapPin className="w-4 h-4 text-[#1E88E5] mt-1 flex-shrink-0" />
                <span>
                  {COMPANY.address.street}
                  <br />
                  {COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.zip}
                </span>
              </a>
              <div className="text-slate-400 text-sm pl-7">
                Hours: {COMPANY.hours}
              </div>
            </address>
            <a
              href="/quote"
              data-testid="footer-get-quote-btn"
              className="mt-6 inline-flex bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-bold tracking-wider uppercase px-6 py-3 rounded-sm text-sm transition-all"
            >
              Get Free Quote
            </a>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-slate-500 text-sm">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <a href="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors" data-testid="footer-link-terms">
              Terms
            </a>
            <a href="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors" data-testid="footer-link-privacy">
              Privacy
            </a>
            <a href="/contract" className="text-slate-500 hover:text-slate-300 text-sm transition-colors" data-testid="footer-link-contract">
              Customer Contract
            </a>
            <div className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-slate-600">
              Veteran Owned · Licensed · Insured
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
